import { useCallback, useEffect, useState } from "react";
import { Check, Copy, ExternalLink, Loader2, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { apiGet, apiPost } from "@/api/client";
import { toast } from "@/components/ui/sonner";

const STATUS_LABELS = {
  pending: { label: "Pending DNS", className: "bg-amber-100 text-amber-950" },
  verified: { label: "DNS verified", className: "bg-emerald-100 text-emerald-950" },
  active: { label: "Live on custom domain", className: "bg-emerald-100 text-emerald-950" },
};

function CopyButton({ value, testId }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      toast.success("Copied to clipboard");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Could not copy — select the text manually");
    }
  };
  return (
    <Button type="button" variant="outline" size="sm" className="shrink-0 rounded-lg" data-testid={testId} onClick={copy}>
      {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
    </Button>
  );
}

export function CustomDomainPanel({ domain, business, onVerified, verifiedMessage }) {
  const [setup, setSetup] = useState(null);
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const normalized = String(domain || "").trim().toLowerCase();
  const successMessage = verifiedMessage || "DNS verified — your custom domain is live";

  const loadSetup = useCallback(async () => {
    if (!normalized) {
      setSetup(null);
      return;
    }
    setLoading(true);
    try {
      const query = normalized !== business?.desired_custom_domain ? `?domain=${encodeURIComponent(normalized)}` : "";
      const result = await apiGet(`/business/domain/setup${query}`);
      setSetup(result);
    } catch {
      toast.error("Could not load DNS instructions");
    } finally {
      setLoading(false);
    }
  }, [normalized, business?.desired_custom_domain]);

  useEffect(() => {
    loadSetup();
  }, [loadSetup, business?.domain_status, business?.desired_custom_domain]);

  const verifyDns = async () => {
    setVerifying(true);
    try {
      const result = await apiPost("/business/domain/verify", {});
      setSetup(result.setup);
      if (result.verified) {
        toast.success(successMessage);
        onVerified?.(result.business);
      } else {
        toast.error("DNS records not found yet — wait for propagation and try again");
      }
    } catch {
      toast.error("Verification failed");
    } finally {
      setVerifying(false);
    }
  };

  if (!normalized) return null;

  const savedDomain = String(business?.desired_custom_domain || "").trim().toLowerCase();
  const domainDirty = savedDomain !== normalized;
  const status = setup?.domain_status || business?.domain_status || "pending";
  const statusMeta = STATUS_LABELS[status] || STATUS_LABELS.pending;

  return (
    <div className="mt-5 rounded-xl border border-[#EA6A3B]/30 bg-white p-5" data-testid="custom-domain-dns-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="font-extrabold text-[#173B2F]" data-testid="custom-domain-dns-title">Connect {normalized}</p>
          <p className="mt-1 text-sm text-muted-foreground" data-testid="custom-domain-dns-subtitle">
            Add these to wherever you manage this domain (like GoDaddy, Namecheap, or Cloudflare), then come back here to confirm.
          </p>
        </div>
        <span className={`rounded-full px-3 py-1 text-xs font-bold ${statusMeta.className}`} data-testid="custom-domain-status-badge">
          {statusMeta.label}
        </span>
      </div>

      {domainDirty ? (
        <p className="mt-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-950" data-testid="custom-domain-save-hint">
          Click <strong>Continue</strong> first to save your domain — this generates the exact values you'll need below.
        </p>
      ) : null}

      {loading && !setup ? (
        <p className="mt-4 flex items-center gap-2 text-sm text-muted-foreground" data-testid="custom-domain-dns-loading">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading DNS records…
        </p>
      ) : setup?.records?.length ? (
        <div className="mt-4 overflow-x-auto rounded-lg border" data-testid="custom-domain-records-table">
          <table className="w-full min-w-[520px] text-left text-sm">
            <thead className="bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="px-3 py-2 font-bold">Record type</th>
                <th className="px-3 py-2 font-bold">Where to add it</th>
                <th className="px-3 py-2 font-bold">What to enter</th>
                <th className="px-3 py-2 font-bold" aria-label="Copy" />
              </tr>
            </thead>
            <tbody>
              {setup.records.map((record, index) => (
                <tr key={`${record.type}-${record.host}-${index}`} className="border-t" data-testid={`custom-domain-record-row-${index}`}>
                  <td className="px-3 py-3 font-bold">{record.type}</td>
                  <td className="px-3 py-3 font-mono text-xs">{record.host}</td>
                  <td className="max-w-[280px] truncate px-3 py-3 font-mono text-xs" title={record.value}>{record.value}</td>
                  <td className="px-3 py-3">
                    <CopyButton value={record.value} testId={`custom-domain-copy-value-${index}`} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      {setup?.notes?.length ? (
        <ul className="mt-4 list-disc space-y-1 pl-5 text-xs text-muted-foreground" data-testid="custom-domain-dns-notes">
          {setup.notes.map((note) => (
            <li key={note}>{note}</li>
          ))}
        </ul>
      ) : null}

      <div className="mt-4 flex flex-wrap items-center gap-3">
        <Button
          type="button"
          className="rounded-lg font-bold"
          data-testid="custom-domain-verify-button"
          disabled={verifying || loading}
          onClick={verifyDns}
        >
          {verifying ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
          Check connection
        </Button>
        <Button type="button" variant="outline" className="rounded-lg" data-testid="custom-domain-refresh-setup-button" disabled={loading} onClick={loadSetup}>
          Refresh
        </Button>
        <a
          href="https://github.com"
          className="inline-flex items-center gap-1 text-xs font-bold text-primary hover:underline"
          data-testid="custom-domain-docs-link"
          onClick={(event) => {
            event.preventDefault();
            toast.message("Step-by-step guide: docs/DNS_SELF_SERVE_GUIDE.md");
          }}
        >
          Need help? <ExternalLink className="h-3 w-3" />
        </a>
      </div>

      {setup?.slug_url ? (
        <p className="mt-3 text-xs text-muted-foreground" data-testid="custom-domain-slug-fallback">
          {status === "active"
            ? <>Customers can also use <strong className="text-[#173B2F]">{setup.slug_url}</strong>.</>
            : <>Until your domain is live, customers can still use <strong className="text-[#173B2F]">{setup.slug_url}</strong>.</>}
        </p>
      ) : null}
    </div>
  );
}
